import { Link } from "react-router-dom";
import {
  ArrowUpRight, Globe, Smartphone, BrainCircuit, LineChart, Server,
  PaintBucket, Check,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { SERVICES } from "@/data/site";
import SEO from "@/components/SEO";

const ICONS = { Globe, Smartphone, BrainCircuit, LineChart, Server, PaintBucket };

export default function Services() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Software Engineering & Digital Product Development",
    "serviceType": "Web, Mobile, AI & Enterprise Solutions",
    "provider": {
      "@type": "Organization",
      "name": "AmagicTech Solutions",
      "url": "https://amagictechsolutions.com"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Capabilities",
      "itemListElement": SERVICES.map((s, idx) => ({
        "@type": "Offer",
        "position": idx + 1,
        "itemOffered": {
          "@type": "Service",
          "name": s.title,
          "description": s.description
        }
      }))
    }
  };

  return (
    <main data-testid="services-page" className="pt-20">
      <SEO 
        title="Our Services"
        description="Explore our software engineering capabilities: custom web applications, mobile apps, enterprise IT solutions, AI integration, cloud DevOps, and UI/UX design."
        url="/services"
        keywords="web application development, mobile app development, AI integration, UI UX design, cloud devops, enterprise software, react native developers"
        schema={serviceSchema}
      />
      <section className="container-tight pb-16">
        <Reveal>
          <SectionLabel testid="services-page-label">Services</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-900 leading-[0.98]">
            Capabilities engineered
            <br /><span className="text-zinc-500">for outcomes.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-zinc-600 text-lg leading-relaxed">
            Six disciplines, one delivery pod. Every engagement starts with discovery,
            then iterates with measurable outcomes — not deliverables for their own sake.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-zinc-200">
        {SERVICES.map((s, idx) => {
          const Icon = ICONS[s.icon];
          const reverse = idx % 2 === 1;
          return (
            <div key={s.id} className="border-b border-zinc-200">
              <div className="container-tight py-16 md:py-24 grid md:grid-cols-12 gap-10">
                <div className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}>
                  <Reveal>
                    <div className="flex items-center gap-4">
                      <span className="font-mono-tech text-xs text-zinc-500">{s.code}</span>
                      <span className="h-px w-12 bg-zinc-800" />
                      <Icon className="w-5 h-5 text-zinc-600" />
                    </div>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl text-zinc-900 tracking-tighter leading-[1.02]">
                      {s.title}
                    </h2>
                  </Reveal>
                </div>
                <div className={`md:col-span-7 ${reverse ? "md:order-1" : ""}`}>
                  <Reveal delay={0.05}>
                    <p className="text-zinc-700 text-lg leading-relaxed">{s.detail}</p>
                    <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-zinc-700 text-[15px]">
                          <Check className="w-4 h-4 mt-1 text-zinc-900 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`services-cta-${s.id}`}
                      className="mt-10 inline-flex items-center gap-2 border border-zinc-300 px-5 py-3 text-sm text-zinc-900 hover:border-zinc-600 hover:bg-white transition-colors"
                    >
                      Talk to us about {s.title.toLowerCase()} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="py-24 md:py-28">
        <div className="container-tight border border-zinc-200 p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <SectionLabel testid="services-cta-label">Engagement model</SectionLabel>
            <h3 className="mt-5 font-display text-3xl md:text-4xl text-zinc-900 tracking-tighter">
              Fixed sprint, retainer, or full build — we&apos;ll suggest what fits.
            </h3>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta-end"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors self-start md:self-auto"
          >
            Request a proposal <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
